import { Button } from '@mui/material';
import { useGame } from 'contexts/Game/Game';
import { usePage } from 'contexts/Page/Page';
import { useSounds } from 'contexts/Sounds/Sounds';
import { useMemo } from 'react';
import { MaterialSymbol } from 'react-material-symbols';
import { Container } from './GameCardButton.styled';

const hiddenLessons = ['1.1', '1.2.1', '1.2.2', '1.2.2.1', '1.2.3', '1.2.3.1', '1.2.4'];

export default function GameCardButton({ cost = 0, round, staticWidth, tabIndex, type }: GameCardButtonProps) {
    const game = useGame();
    const page = usePage();
    const sounds = useSounds();

    const { cardButtonLesson, disableCardButton, hideCardButton, incorrectActionLesson, skipDeal, showRoundCosts } =
        game.lessonProps;

    const className = useMemo(() => {
        return 'fill' + (showRoundCosts ? ' static' : '');
    }, [showRoundCosts]);

    const disabled = useMemo(() => {
        return showRoundCosts || (!cardButtonLesson && (disableCardButton || cost > game.budget));
    }, [cardButtonLesson, cost, disableCardButton, game.budget, showRoundCosts]);

    const hide = useMemo(() => {
        if (showRoundCosts) {
            return round === 'opponents';
        }
        return game.round !== round || hideCardButton || !page.lesson || hiddenLessons.includes(page.lesson);
    }, [game.round, hideCardButton, page.lesson, round, showRoundCosts]);

    const label = useMemo(() => {
        if (type === 'showOpponents') {
            return 'Show';
        } else if (showRoundCosts) {
            return `$${cost}`;
        } else if (page.lessonParent === 1) {
            return 'Deal';
        }
        return `Pay $${cost}`;
    }, [cost, page.lessonParent, showRoundCosts, type]);

    const sx = useMemo(() => {
        return {
            width: showRoundCosts ? staticWidth : undefined,
        };
    }, [showRoundCosts, staticWidth]);

    function handleClick() {
        if (!game.loading && game.ready && !showRoundCosts) {
            sounds.play('submit');

            // Perform action
            const incorrect = incorrectActionLesson && !game.shouldPay;
            if (incorrect) {
                game.interceptAction(type, cardButtonLesson, skipDeal);
            } else if (!disableCardButton) {
                if (type === 'showOpponents') {
                    game.showOpponents();
                } else {
                    game.pay(type, !cardButtonLesson, skipDeal);
                }
            }

            // Show lesson
            if (incorrect) {
                page.show(incorrectActionLesson);
            } else if (cardButtonLesson) {
                page.show(cardButtonLesson);
            }
        }
    }

    if (hide) {
        return null;
    }

    return (
        <Container>
            <Button
                className={className}
                disabled={disabled}
                startIcon={<MaterialSymbol aria-hidden icon="payments" size={23} />}
                sx={sx}
                tabIndex={tabIndex}
                variant="text"
                onClick={handleClick}
            >
                {label}
            </Button>
        </Container>
    );
}
